// src/pages/client/news/NewsDetail.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Container from "../../../components/Container";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

export default function NewsDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/api/news/detail/${id}`);
      const resultCode = res.data?.resultCode;

      if (resultCode !== "200" && resultCode !== 200) {
        setError(res.data?.resultMessage || "소식 상세를 불러오지 못했습니다.");
        setItem(null);
        return;
      }

      setItem(res.data?.data || null);
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "소식 상세를 불러오는 중 오류가 발생했습니다."
      );
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderContent = () => {
    if (!item?.content) return null;
    const safeHtml = DOMPurify.sanitize(item.content);
    return (
      <div
        className="prose max-w-none text-sm leading-relaxed text-slate-800 prose-a:text-blue-600 prose-a:underline prose-img:rounded-xl prose-img:shadow-sm md:text-base"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  };

  return (
    <main className="bg-white">
      {/* 상단 Hero 영역 (간단 버전) */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100 via-white to-white" />
        <Container className="py-10 md:py-14">
          <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-[11px]">
            {t("news.badge", "Infinity Training Center 소식")}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-blue-900 md:text-4xl">
            {t("news.title", "소식")}
          </h1>
        </Container>
      </section>

      <Container className="py-8 md:py-10">
        {/* 상태 표시 */}
        {loading && (
          <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
            {t("news.loading", "소식을 불러오는 중입니다...")}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">
            {error}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => nav("/news")}
                className="rounded-lg border bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {t("news.backToList", "목록으로 돌아가기")}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && !item && (
          <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
            {t("news.notFound", "해당 소식을 찾을 수 없습니다.")}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => nav("/news")}
                className="rounded-lg border bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {t("news.backToList", "목록으로 돌아가기")}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && item && (
          <article className="rounded-2xl border bg-white p-6 shadow-sm md:p-8">
            {/* 제목 + 메타 */}
            <header className="border-b pb-4 md:pb-5">
              <div className="mb-1 text-xs text-slate-500">
                {fmtDate(item.createdAt)}
              </div>
              <h2 className="text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                {item.title}
              </h2>
            </header>

            {/* 내용 */}
            <section className="mt-6">{renderContent()}</section>

            {/* 하단: 작성일 / 수정일 */}
            <footer className="mt-8 border-t pt-4 text-xs text-slate-500">
              <div>
                {t("news.createdAt", "작성일")} : {fmtDate(item.createdAt)}
              </div>
              {item.updatedAt && item.updatedAt !== item.createdAt && (
                <div className="mt-1">
                  {t("news.updatedAt", "수정일")} : {fmtDate(item.updatedAt)}
                </div>
              )}
            </footer>

            {/* 버튼 영역 */}
            <div className="mt-6 flex justify-end">
              <Link
                to="/news"
                className="inline-flex items-center justify-center rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {t("news.backToList", "목록으로")}
              </Link>
            </div>
          </article>
        )}
      </Container>
    </main>
  );
}
